import { ButtonBase, styled, type SxProps, type Theme } from '@mui/material';
import { useTransition, type ComponentProps } from 'react';
import type { MaybePromise } from 'shared/types';

const StyledButton = styled(ButtonBase, {
  shouldForwardProp: (p) => p !== 'color' && p !== 'fullWidth',
})<{
  color: 'orange' | 'yellow' | 'blue' | 'transparent';
  size?: 'normal' | 'small';
  fullWidth?: boolean;
}>(({ theme }) => ({
  width: '100%',
  border: '2px solid',
  borderRadius: '99999px',
  padding: '10px 36px',
  backgroundColor: 'var(--main)',
  borderColor: 'var(--main)',
  color: 'white',
  transition: theme.transitions.create(['color', 'background-color', 'opacity']),

  '&:hover': {
    backgroundColor: 'white',
    borderColor: 'var(--main)',
    color: 'var(--main)',
  },

  [theme.breakpoints.up('sm')]: {
    padding: '15px 36px',
  },

  '&:disabled': {
    opacity: 0.5,
  },

  variants: [
    // Color
    {
      props: { color: 'orange' },
      style: { '--main': '#FA8331' },
    },
    {
      props: { color: 'blue' },
      style: { '--main': theme.palette.primary.main },
    },
    {
      props: { color: 'yellow' },
      style: {
        '--main': theme.palette.secondary.main,
        color: theme.palette.text.default,
        '&:hover': { color: theme.palette.text.default },
      },
    },
    {
      props: { color: 'transparent' },
      style: {
        '--main': 'transparent',
        color: theme.palette.text.default,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderColor: '#CEE2FF',
        '&:hover': {
          color: theme.palette.text.default,
          backgroundColor: '#EFF7FF',
          borderColor: '#CEE2FF',
        },
      },
    },

    // Size
    {
      props: (p) => !p.size || p.size === 'normal',
      style: {
        // Prevent media query conflicts
        '&': { ...theme.typography.button1n },
      },
    },
    {
      props: { size: 'small' },
      style: {
        // Prevent media query conflicts
        '&': { ...theme.typography.body4n },
      },
    },

    // Full width
    {
      props: (p) => !p.fullWidth,
      style: {
        maxWidth: '320px',
        [theme.breakpoints.up('sm')]: {
          maxWidth: '400px',
          padding: '15px 36px',
        },
      },
    },
    {
      props: { fullWidth: true },
      style: { maxWidth: 'none' },
    },
  ],
}));

export type ButtonProps = {
  action?: (e: React.MouseEvent<HTMLButtonElement>) => MaybePromise<void>;
  /** @deprecated Use tailwind with !important instead */
  sx?: SxProps<Theme>;
} & Omit<ComponentProps<typeof StyledButton>, 'action'>;

export const Button = (props: ButtonProps) => {
  const [isPending, startTransition] = useTransition();
  const { action, disabled, onClick, ...rest } = props;

  return (
    <StyledButton
      {...rest}
      disabled={isPending || disabled}
      onClick={(e) => {
        if (action) {
          startTransition(async () => {
            await action(e);
          });
        }
        onClick?.(e);
      }}
    />
  );
};
